import { useTranslation } from 'react-i18next';

import { isCatalogedIssueCode, issueCodeKeys } from './catalog';
import type { IssuePresentation } from './types';

/**
 * Localized title/message for one audit issue (spec-13 §5/§7).
 *
 * - Cataloged system codes translate through the catalog keys; raw backend
 *   English is never the primary text.
 * - Manual issues show the localized category title plus the authored
 *   description as a clearly-labeled report note.
 * - Unknown codes fall back to a localized generic system-issue message; the
 *   code itself belongs in the technical disclosure, not here.
 */
export const useIssueText = (
  presentation: IssuePresentation,
  opts?: {
    /** Viewer may open the technical disclosure — only then may generic
     *  copy point at it (round-5 item 9). */
    technical?: boolean;
  },
) => {
  const { t } = useTranslation(['issues']);
  const code = presentation.issue_code;
  const cataloged = isCatalogedIssueCode(code);
  const keys = issueCodeKeys(code);
  const params = (presentation.message_params ?? {}) as Record<string, unknown>;

  const title = cataloged ? t(keys.title, { ...params }) : t('efs.issues.generic.title');
  // Unknown codes: a non-technical operator gets actionable guidance they can
  // follow; the technical variant additionally points at the disclosure.
  const message = cataloged
    ? t(keys.message, { ...params })
    : t(opts?.technical ? 'efs.issues.generic.message_technical' : 'efs.issues.generic.message');
  const action =
    cataloged && presentation.next_action_key !== null
      ? t(keys.action, { ...(presentation.next_action_params ?? {}) })
      : null;

  return { title, message, action, cataloged };
};

export const AuditIssueMessage = ({
  presentation,
  className,
  showTechnicalDetails = false,
}: {
  presentation: IssuePresentation;
  className?: string;
  /** Whether the viewer can open the technical disclosure. */
  showTechnicalDetails?: boolean;
}) => {
  const { t } = useTranslation(['issues']);
  const { title, message } = useIssueText(presentation, { technical: showTechnicalDetails });
  const isManual = presentation.message_origin === 'manual';

  return (
    <div className={className}>
      <p className="text-sm font-medium text-efs-text-primary">{title}</p>
      {isManual ? (
        presentation.legacy_description ? (
          <p className="mt-0.5 text-sm text-efs-text-primary">
            <span className="mr-1 rounded bg-efs-bg-subtle px-1 py-0.5 text-[11px] text-efs-text-muted">
              {t('efs.issues.manual.note_label')}
            </span>
            <span className="whitespace-pre-wrap break-words">
              {presentation.legacy_description}
            </span>
          </p>
        ) : null
      ) : (
        <p className="mt-0.5 text-sm text-efs-text-muted">{message}</p>
      )}
    </div>
  );
};
